import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/data';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminAccounts.css';

const AdminAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editForm, setEditForm] = useState({
    account_number: '',
    balance: '',
    currency: 'RUB',
    is_active: true,
    percentage: ''
  });

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchAccounts();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate, currentPage, typeFilter, statusFilter]);

  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(typeFilter !== 'all' && { type: typeFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(`${API_URL}/api/admin/accounts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAccounts(response.data.accounts || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Ошибка загрузки счетов');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAccounts();
  };

  const handleStatusToggle = async (accountId, currentStatus, accountType) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = currentStatus === 'active' ? false : true;
      
      await axios.put(`${API_URL}/api/admin/accounts/${accountId}/status`, 
        { isActive: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchAccounts();
    } catch (error) {
      console.error('Error updating account status:', error);
      setError('Ошибка обновления статуса счета');
    }
  };

  const handleViewDetails = (account) => {
    setSelectedAccount(account);
    setShowAccountDetails(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setEditForm({
      account_number: account.account_number || '',
      balance: account.balance || '', // Используем balance для торговых счетов
      currency: account.currency || 'RUB',
      is_active: account.status === 'active',
      percentage: account.percentage || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Подготавливаем данные для отправки
      const updateData = {
        account_number: editForm.account_number,
        balance: parseFloat(editForm.balance),
        percentage: parseFloat(editForm.percentage),
        currency: editForm.currency,
        status: editForm.is_active ? 'active' : 'inactive'
      };
      
      // Всегда используем API для торговых счетов
      await axios.put(`${API_URL}/api/admin/trading-accounts/${editingAccount.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowEditModal(false);
      setEditingAccount(null);
      setEditForm({
        account_number: '',
        balance: '',
        currency: 'RUB',
        is_active: true,
        percentage: ''
      });
      await fetchAccounts();
    } catch (error) {
      console.error('Error updating account:', error);
      setError('Ошибка обновления счета');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'RUB') => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getAccountType = (account) => {
    return 'trading'; // Все счета теперь торговые
  };

  const getAccountTypeText = (account) => {
    return 'Торговый';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'blocked': return 'danger';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Активный';
      case 'blocked': return 'Заблокирован';
      case 'closed': return 'Закрыт';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <div className="admin-accounts">
        <div className="admin-header">
          <div className="admin-header__logo">
            <img src="/assets/img/logo.svg" alt="Pro Investor" />
          </div>
          <div className="admin-header__title">Управление счетами</div>
          <div className="admin-header__actions">
            <button className="admin-header__back" onClick={() => navigate('/admin')}>
              <span className="--icon-arrow-left"></span>
              Назад
            </button>
            <button className="admin-header__logout" onClick={() => logoutAdmin()}>
              <span className="--icon-log-out"></span>
              Выйти
            </button>
          </div>
        </div>
        <div className="admin-loading">
          <div className="admin-loading__spinner"></div>
          <p>Загрузка счетов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-accounts">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Управление счетами</div>
        <div className="admin-header__actions">
          <button className="admin-header__back" onClick={() => navigate('/admin')}>
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={() => logoutAdmin()}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-content__header">
          <h1 className="admin-title">Счета</h1>
          <button className="admin-refresh-btn" onClick={fetchAccounts}>
            <span className="--icon-arrow"></span>
            Обновить
          </button>
        </div>

        {/* Filters */}
        <div className="admin-filters">
          <form onSubmit={handleSearch} className="admin-search">
            <input
              type="text"
              placeholder="Поиск по номеру счета, имени..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-search__input"
            />
            <button type="submit" className="admin-search__btn">
              <span className="--icon-search"></span>
            </button>
          </form>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="admin-filter__select"
          >
            <option value="all">Все типы</option>
            <option value="banking">Банковские</option>
            <option value="trading">Торговые</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-filter__select"
          >
            <option value="all">Все статусы</option>
            <option value="active">Активные</option>
            <option value="blocked">Заблокированные</option>
            <option value="closed">Закрытые</option>
          </select>
        </div>

        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button onClick={fetchAccounts} className="admin-btn admin-btn--primary">
              Попробовать снова
            </button>
          </div>
        )}

        {/* Accounts Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Тип</th>
                <th>Номер счета</th>
                <th>Владелец</th>
                <th>Прибыль</th>
                <th>Процент</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.id}</td>
                  <td>
                    <span className={`admin-badge admin-badge--${getAccountType(account)}`}>
                      {getAccountTypeText(account)}
                    </span>
                  </td>
                  <td className="admin-account-number">
                    {account.account_number}
                  </td>
                  <td>
                    <div className="admin-user-info">
                      <div className="admin-user-info__name">
                        {account.first_name} {account.last_name}
                      </div>
                      <div className="admin-user-info__email">{account.email}</div>
                    </div>
                  </td>
                  <td className="admin-amount">
                    {formatCurrency(account.balance, account.currency)}
                  </td>
                  <td className="admin-percentage">
                    {account.percentage || 0}%
                  </td>
                  
                  <td>{formatDate(account.created_at)}</td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-btn admin-btn--sm admin-btn--info"
                        onClick={() => handleViewDetails(account)}
                      >
                        Детали
                      </button>
                      <button
                        className="admin-btn admin-btn--sm admin-btn--warning"
                        onClick={() => handleEdit(account)}
                      >
                        Редактировать
                      </button>
                      <button
                        className={`admin-btn admin-btn--sm ${
                          account.status === 'active' ? 'admin-btn--danger' : 'admin-btn--success'
                        }`}
                        onClick={() => handleStatusToggle(account.id, account.status, getAccountType(account))}
                      >
                        {account.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            <span className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </span>
            <button
              className="admin-pagination__btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingAccount && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setShowEditModal(false)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Редактировать счет #{editingAccount.id}</h2>
                <button
                  className="admin-modal__close"
                  onClick={() => setShowEditModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <form onSubmit={handleEditSubmit} className="admin-edit-form">
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="account_number">
                        Номер счета *
                      </label>
                      <input
                        type="text"
                        id="account_number"
                        name="account_number"
                        value={editForm.account_number}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="currency">Валюта</label>
                      <select
                        id="currency"
                        name="currency"
                        value={editForm.currency}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                      >
                        <option value="RUB">RUB</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="CNY">CNY</option>
                      </select>
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="balance">
                        Баланс *
                      </label>
                      <input
                        type="number"
                        id="balance"
                        name="balance"
                        step="0.01"
                        value={editForm.balance}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="percentage">Процент</label>
                      <input
                        type="number"
                        id="percentage"
                        name="percentage"
                        step="0.01"
                        min="0"
                        max="100"
                        value={editForm.percentage}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                      />
                    </div>
                  </div>


                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__checkbox">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={editForm.is_active}
                          onChange={handleEditFormChange}
                        />
                        <span>Активный счет</span>
                      </label>
                    </div>
                  </div>

                  <div className="admin-edit-form__actions">
                    <button
                      type="button"
                      className="admin-btn admin-btn--secondary"
                      onClick={() => setShowEditModal(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="admin-btn admin-btn--primary"
                    >
                      Сохранить
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Account Details Modal */}
        {showAccountDetails && selectedAccount && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setShowAccountDetails(false)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Детали счета #{selectedAccount.id}</h2>
                <button
                  className="admin-modal__close"
                  onClick={() => setShowAccountDetails(false)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <div className="admin-account-details">
                  <div className="admin-account-details__section">
                    <h3>Основная информация</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">ID счета:</span>
                      <span className="admin-detail-value">#{selectedAccount.id}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Тип:</span>
                      <span className="admin-detail-value">
                        <span className={`admin-badge admin-badge--${getAccountType(selectedAccount)}`}>
                          {getAccountTypeText(selectedAccount)}
                        </span>
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Номер счета:</span>
                      <span className="admin-detail-value admin-account-number">
                        {selectedAccount.account_number}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Валюта:</span>
                      <span className="admin-detail-value">{selectedAccount.currency}</span>
                    </div>
                    
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Дата создания:</span>
                      <span className="admin-detail-value">{formatDate(selectedAccount.created_at)}</span>
                    </div>
                  </div>

                  <div className="admin-account-details__section">
                    <h3>Финансовая информация</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">
                        Прибыль:
                      </span>
                      <span className="admin-detail-value admin-amount">
                        {formatCurrency(selectedAccount.balance, selectedAccount.currency)}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Процент:</span>
                      <span className="admin-detail-value admin-percentage">
                        {selectedAccount.percentage || 0}%
                      </span>
                    </div>
                  </div>

                  <div className="admin-account-details__section">
                    <h3>Информация о владельце</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Имя:</span>
                      <span className="admin-detail-value">
                        {selectedAccount.first_name} {selectedAccount.last_name}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Email:</span>
                      <span className="admin-detail-value">{selectedAccount.email}</span>
                    </div>
                  </div>

                  {selectedAccount.bank && (
                    <div className="admin-account-details__section">
                      <h3>Банковская информация</h3>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Банк:</span>
                        <span className="admin-detail-value">{selectedAccount.bank}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAccounts;